import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Link as PhosphorLink,
  LinkBreak,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Lightning,
  Eye,
  ShieldCheck,
} from '@phosphor-icons/react';
import {
  marketplaceApi,
  StoreSupplierConnection,
  OAuthAuthorization,
} from '@dukene/shared/api/marketplace';
import { confirmAction } from '../../components/modals/ConfirmModal';
import { useAuthStore, useNotificationStore } from '@dukene/shared';
import { SuppliersSubNav } from './Suppliers';

export default function ConnectedSuppliers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [connections, setConnections] = useState<StoreSupplierConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [oauthStatuses, setOauthStatuses] = useState<Record<string, OAuthAuthorization>>({});

  useEffect(() => {
    const oauthStatus = searchParams.get('oauth');
    const supplierName = searchParams.get('supplier');
    const { addNotification } = useNotificationStore.getState();

    if (oauthStatus === 'success' && supplierName) {
      addNotification({ type: 'success', message: `Successfully connected to ${supplierName}!` });
      window.history.replaceState({}, '', '/suppliers/connected');
    } else if (oauthStatus === 'error') {
      addNotification({
        type: 'error',
        message: 'OAuth authorization failed. Please try again.',
      });
      window.history.replaceState({}, '', '/suppliers/connected');
    }

    loadConnections();
  }, [searchParams]);

  const loadConnections = async () => {
    if (!user?.storeId) return;

    try {
      setLoading(true);
      const data = await marketplaceApi.getConnectedSuppliers(user.storeId);
      setConnections(data || []);

      const oauthSuppliers = (data || []).filter((c) => c.supplier?.requiresOAuth);
      const statuses: Record<string, OAuthAuthorization> = {};

      await Promise.all(
        oauthSuppliers.map(async (connection) => {
          try {
            const { authorization } = await marketplaceApi.getOAuthStatus(
              user.storeId!,
              connection.supplierId
            );
            statuses[connection.supplierId] = authorization;
          } catch (error) {
            console.error(`Failed to load OAuth status for ${connection.supplierId}:`, error);
          }
        })
      );

      setOauthStatuses(statuses);
    } catch (error: any) {
      const { addNotification } = useNotificationStore.getState();
      addNotification({
        type: 'error',
        message: error.message || 'Failed to load connections',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (connection: StoreSupplierConnection) => {
    if (!user?.storeId) return;

    const confirmed = await confirmAction({
      title: 'Disconnect Supplier',
      message: `Are you sure you want to disconnect from ${connection.supplier?.name}? This will not delete your imported products.`,
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await marketplaceApi.revokeOAuth(user.storeId, connection.supplierId);
      const { addNotification } = useNotificationStore.getState();
      addNotification({
        type: 'success',
        message: `Disconnected from ${connection.supplier?.name}`,
      });
      loadConnections();
    } catch (error: any) {
      const { addNotification } = useNotificationStore.getState();
      addNotification({
        type: 'error',
        message: error.message || 'Failed to disconnect supplier',
      });
    }
  };

  const getStatusBadge = (status?: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
          <CheckCircle weight="fill" className="w-3 h-3" />
          Connected
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <Clock weight="fill" className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
        <XCircle weight="fill" className="w-3 h-3" />
        {s || 'Inactive'}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      <SuppliersSubNav activeTab="CONNECTED" />

      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Connected Marketplace Suppliers
          </h1>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Manage live API integrations, OAuth tokens, and product inventory syncing
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/suppliers/marketplace')}
          className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors shrink-0"
        >
          <PhosphorLink weight="bold" className="w-3.5 h-3.5" />
          Browse Marketplace
        </button>
      </div>

      {/* Directory Table */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : connections.length === 0 ? (
          <div className="text-center py-16 px-6">
            <ShieldCheck weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Connected Marketplace Suppliers
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
              Explore our verified B2B marketplace to integrate automated catalog dropship suppliers.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Supplier Partner</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4">API Sync Status</th>
                  <th className="py-2.5 px-4">Catalog Sync</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {connections.map((conn) => {
                  const supplier = conn.supplier || {};
                  const oauth = oauthStatuses[conn.supplierId];

                  return (
                    <tr
                      key={conn.id}
                      className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#006838] font-bold text-xs shrink-0">
                            {supplier.name?.slice(0, 2).toUpperCase() || 'SP'}
                          </div>
                          <div>
                            <p className="font-semibold text-[#202223] dark:text-white">
                              {supplier.name}
                            </p>
                            {supplier.categories?.[0] && (
                              <span className="text-[10px] text-[#5C5F62]">
                                {supplier.categories[0]}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="py-3 px-4">{getStatusBadge(conn.status)}</td>

                      <td className="py-3 px-4">
                        {supplier.requiresOAuth ? (
                          oauth?.isAuthorized ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F1DF] text-[#006838]">
                              <Lightning weight="fill" className="w-3 h-3" />
                              OAuth Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700">
                              Auth Required
                            </span>
                          )
                        ) : (
                          <span className="text-[#8C9196] text-[11px]">Direct API</span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 text-[#5C5F62]">
                          <Package weight="fill" className="w-3.5 h-3.5 text-[#006838]" />
                          <span>Auto-sync enabled</span>
                        </div>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/suppliers/marketplace/${conn.supplierId}`)}
                            className="p-1.5 rounded hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
                            title="View integration details"
                          >
                            <Eye weight="bold" className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDisconnect(conn)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 transition-colors"
                            title="Disconnect supplier"
                          >
                            <LinkBreak weight="bold" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
